import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { getBusinessStatus } from "../api/onboarding";
import { completeOperatorMfa, login as loginApi } from "../api/auth";
import { buildHostUrl } from "../config/hostRouting";
import {
  isBillingBypassedForSuperAdmin,
  isBillingEnforcedRole,
} from "../utils/billingAccess";
import { getEllyannaAccessGate } from "../utils/ellyannaAccessGate";
import { getMarketplaceAuthenticatedLandingPath } from "../utils/marketplaceShellRouting";
import { isMarketplaceOperatorRole } from "../utils/marketplaceOperatorAccess";
import { getTechnicianLandingPath } from "../utils/technicianRouting";
import { notifyOnboardingStatusChange } from "../utils/onboarding";
import { getMarketplaceOperatorBookingsPath } from "../utils/marketplaceOperatorRoutes";

const TECHNICIAN_REFRESH_TIMEOUT_MS = 3000;
const LOGIN_FLOW_TIMEOUT_MS = 35000;
const LOGIN_ERROR_MESSAGES = {
  INVALID_CREDENTIALS:
    "The email, username, or password is incorrect.",
  SALON_NOT_FOUND: "That Salon ID was not found.",
  INDEPENDENT_PROFESSIONAL_NOT_FOUND:
    "That Independent Professional ID is not linked to this account.",
  USER_NOT_IN_SALON:
    "This account is not linked to that salon. Use the correct Salon ID or ask a manager to add you.",
  SALON_ACCESS_INACTIVE:
    "Your access to this salon is inactive. Ask a manager to reactivate your account.",
};

const MARKETPLACE_LOGIN_CONTENT = {
  badge: "ELLYANNA MYBOOTHSHARE",
  headline: "Welcome to your workspace and business hub.",
  body:
    "Sign in to manage your workspace bookings, client activity, and business setup in one place. Whether you are an independent professional or a salon owner, MyBoothShare helps you stay organized and ready for your next opportunity.",
  infoCards: [
    {
      title: "Independent Professional",
      body:
        "Find workspaces, manage your bookings, and keep your client work organized as you grow your business.",
    },
    {
      title: "Salon Owner / Operator",
      body:
        "Manage workspace listings, booking requests, leases, and rental settings for your salon.",
    },
  ],
  primaryLinkLabel: "Open Ellyanna Booking",
  secondaryLinkLabel: "Create account",
  eyebrow: "MYBOOTHSHARE SIGN-IN",
  formHeadline: "Sign in to MyBoothShare",
  formBody: "Choose how you want to sign in today.",
  selectorOptions: ["independent_professional", "salon"],
  idHelpers: {
    independent_professional: "Use the Professional ID from your account setup.",
    salon: "Use your Salon ID to manage your salon's workspace activity.",
  },
  infoBox:
    "Your MyBoothShare and Ellyanna Booking access work together, so you can move between workspace rentals and business management when needed.",
  footerLead: "New independent professional?",
  footerLinkLabel: "Create account",
};

const ACCOUNTING_LOGIN_CONTENT = {
  badge: "ELLYANNA SALONBUSINESSCPA",
  headline: "Welcome to your accounting and tax hub.",
  body:
    "Sign in to organize your SalonBusinessCPA profile, upload accounting records, review tax and payroll checklists, and keep your salon business ready for personal tax, corporate tax, GST/HST, bookkeeping, and financial statements.",
  infoCards: [
    {
      title: "Independent Professional",
      body:
        "Organize your personal tax, business records, GST/HST, receipts, tips, commissions, and owner pay details.",
    },
    {
      title: "Salon Owner / Operator",
      body:
        "Manage accounting records, payroll documents, GST/HST support, corporate tax preparation, and financial statement checklists for your salon.",
    },
  ],
  primaryLinkLabel: "Open Ellyanna Booking",
  secondaryLinkLabel: "Create account",
  eyebrow: "SALONBUSINESSCPA SIGN-IN",
  formHeadline: "Sign in to SalonBusinessCPA",
  formBody: "Choose how you want to sign in today.",
  selectorOptions: ["independent_professional", "salon"],
  idHelpers: {
    independent_professional:
      "Use the same Professional ID from your Ellyanna Booking account setup.",
    salon: "Use the same Salon ID from your Ellyanna Booking salon account.",
  },
  infoBox:
    "Your SalonBusinessCPA and Ellyanna Booking access work together. Use Ellyanna Booking for daily salon operations, then use SalonBusinessCPA to organize accounting, tax, payroll, and bookkeeping records.",
  footerLead: "New to Ellyanna?",
  footerLinkLabel: "Create account",
};

const OPS_LOGIN_CONTENT = {
  badge: "ELLYANNA BOOKING",
  headline: "Welcome back to your client booking hub.",
  body:
    "Sign in to manage appointments, client records, services, checkout, reports, and the daily details that keep your business running smoothly.",
  infoCards: [
    {
      title: "Salon Owners / Operators",
      body:
        "Keep your appointment book, team schedules, client profiles, services, checkout, reports, and salon setup organized in one place.",
    },
    {
      title: "Independent Professionals",
      body:
        "Manage your client bookings, service menu, sales, schedule, and business setup with tools made for professionals working on their own.",
    },
  ],
  primaryLinkLabel: "Open MyBoothShare",
  tertiaryLinkLabel: "Open SalonBusinessCPA",
  secondaryLinkLabel: "Create account",
  eyebrow: "ELLYANNA BOOKING SIGN-IN",
  formHeadline: "Sign in to Ellyanna Booking",
  formBody: "Choose how you want to sign in today.",
  selectorOptions: ["salon", "independent_professional"],
  idHelpers: {
    salon: "Use your Salon ID to access your salon account.",
    independent_professional:
      "Use your Professional ID to access your independent professional account.",
  },
  infoBox:
    "Use Ellyanna Booking to manage the daily work that keeps your business moving: appointments, clients, services, sales, and setup.",
  footerLead: "New to Ellyanna Booking?",
  footerLinkLabel: "Create account",
};

function OperatorLoginForm({
  accountMode,
  error,
  forgotPasswordTarget,
  identifier,
  loading,
  onSubmit,
  password,
  salonId,
  setAccountMode,
  setIdentifier,
  setPassword,
  setSalonId,
  loginContent,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const inputClass =
    "mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200";
  const passwordInputClass =
    "mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 pr-11 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200";
  const labelClass =
    "block text-xs font-medium uppercase tracking-wide text-slate-700";
  const isIndependentProfessionalMode =
    accountMode === "independent_professional";
  const idLabel = isIndependentProfessionalMode
    ? "Independent Professional ID"
    : "Salon ID";
  const idPlaceholder = isIndependentProfessionalMode ? "Example: 6" : "Example: 123";
  const idHelper =
    loginContent.idHelpers[accountMode] ||
    loginContent.idHelpers.salon;
  const selectorLabels = {
    salon: "Salon ID",
    independent_professional: "Independent Professional ID",
  };
  const selectorButtonClass = (active) =>
    `rounded-lg px-3 py-2 text-xs font-semibold transition ${
      active
        ? "bg-white text-slate-900 shadow-sm"
        : "text-slate-600 hover:text-slate-900"
    }`;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {error && (
        <div
          className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-700"
        >
          {error}
        </div>
      )}
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-700">
          Sign in as
        </div>
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
          {loginContent.selectorOptions.map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={accountMode === mode}
              onClick={() => setAccountMode(mode)}
              className={selectorButtonClass(accountMode === mode)}
            >
              {selectorLabels[mode]}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label htmlFor="login-salon-id" className={labelClass}>
          {idLabel}
        </label>
        <input
          id="login-salon-id"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          required
          value={salonId}
          onChange={(e) => setSalonId(e.target.value)}
          className={inputClass}
          placeholder={idPlaceholder}
        />
        <p className="mt-1 text-[11px] leading-4 text-slate-500">
          {idHelper}
        </p>
      </div>
      <div>
        <label htmlFor="login-identifier" className={labelClass}>
          Email or username
        </label>
        <input
          id="login-identifier"
          type="text"
          required
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          className={inputClass}
          placeholder="Enter email or username"
        />
      </div>
      <div>
        <label htmlFor="login-password" className={labelClass}>
          Password
        </label>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={passwordInputClass}
            placeholder="********"
          />
          <button
            type="button"
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            onClick={() => setShowPassword((current) => !current)}
            className="absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-200"
          >
            {showPassword ? (
              <svg
                aria-hidden="true"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M3 3l18 18" />
                <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                <path d="M9.9 5.2A10.5 10.5 0 0 1 12 5c6 0 9.5 7 9.5 7a16.4 16.4 0 0 1-3.1 4.1" />
                <path d="M6.5 6.5A16.1 16.1 0 0 0 2.5 12s3.5 7 9.5 7a10.8 10.8 0 0 0 4.3-.9" />
              </svg>
            ) : (
              <svg
                aria-hidden="true"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-600">
        {loginContent.infoBox}
      </div>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span aria-hidden="true" />
        <Link
          to={forgotPasswordTarget}
          className="font-medium text-rose-600 hover:text-rose-700"
        >
          Forgot your password?
        </Link>
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-xl bg-[#2f3566] py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#252a53] disabled:cursor-not-allowed disabled:opacity-70"
      >
        {loading ? "Signing In..." : "Sign In"}
      </button>
    </form>
  );
}

function OperatorMfaForm({
  emailMasked,
  error,
  loading,
  code,
  rememberDevice,
  setCode,
  setRememberDevice,
  onSubmit,
  onBack,
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
        We emailed a verification code to {emailMasked || "your account email"}.
      </div>
      <div>
        <label
          htmlFor="operator-login-mfa-code"
          className="block text-xs font-medium uppercase tracking-wide text-slate-700"
        >
          Verification code
        </label>
        <input
          id="operator-login-mfa-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(event) => setCode(event.target.value)}
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
          placeholder="Enter the 6-digit code"
        />
      </div>
      <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={rememberDevice}
          onChange={(event) => setRememberDevice(event.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#2f3566] focus:ring-[#2f3566]"
        />
        <span>
          Trust this device for the next 24 hours. If you leave this unchecked,
          we&apos;ll email a verification code every time you sign in.
        </span>
      </label>
      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-xl bg-[#2f3566] py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#252a53] disabled:cursor-not-allowed disabled:opacity-70"
      >
        {loading ? "Verifying..." : "Continue"}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="w-full rounded-xl border border-slate-300 bg-white py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
      >
        Back to sign in
      </button>
    </form>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const runtimeBrand = useBrand();
  const {
    login,
    refreshTechnicianOnboarding,
    token,
    user,
    loading: authLoading,
    technicianStatus,
  } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const initialSalonId = (searchParams.get("salonId") || "").trim();
  const initialProfessionalId = (
    searchParams.get("professionalId") ||
    searchParams.get("independentProfessionalId") ||
    ""
  ).trim();
  const initialIdentifier = (
    searchParams.get("email") ||
    searchParams.get("identifier") ||
    searchParams.get("username") ||
    ""
  ).trim();
  const initialAccountMode =
    initialProfessionalId || /^ip(?:-|[0-9])/i.test(initialSalonId)
      ? "independent_professional"
      : runtimeBrand.isMarketplaceHost || runtimeBrand.isAccountingHost
      ? "independent_professional"
      : "salon";

  const [loginAccountMode, setLoginAccountMode] = useState(initialAccountMode);
  const [salonId, setSalonId] = useState(
    (initialProfessionalId || initialSalonId).replace(/\D/g, "")
  );
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const loginWatchdogRef = useRef(null);
  const next = searchParams.get("next");
  const normalizedAccountId = salonId.trim();
  const normalizedRole = (user?.role || "").toLowerCase();
  const isMarketplaceLogin = runtimeBrand.isMarketplaceHost;
  const isAccountingLogin = runtimeBrand.isAccountingHost;
  const forgotPasswordTarget =
    loginAccountMode === "independent_professional"
      ? isAccountingLogin
        ? "/forgot-password"
        : "/marketplace/forgot-password"
      : normalizedAccountId
      ? `/forgot-password?salonId=${encodeURIComponent(normalizedAccountId)}`
    : "/forgot-password";
  const isAuthenticated = Boolean(token && user);
  const authenticatedDestination = (() => {
    if (!isAuthenticated) return null;
    if (user?.must_change_password) return "/change-password";
    if (next) return next;
    if (isMarketplaceLogin) {
      return getMarketplaceAuthenticatedLandingPath({
        role: normalizedRole,
        user,
      });
    }
    if (isAccountingLogin) {
      return "/salonbusinesscpa";
    }
    if (normalizedRole === "technician") {
      return getTechnicianLandingPath({
        hasSelectedSalon:
          technicianStatus?.has_selected_salon ?? Boolean(user?.salon_id),
        staffSetupComplete: technicianStatus?.staff_setup_complete ?? false,
        profileComplete: technicianStatus?.profile_complete ?? false,
      });
    }
    if (user?.superadmin_mode) {
      return "/superadmin";
    }
    const authenticatedAccessGate = getEllyannaAccessGate(normalizedRole, user);
    if (authenticatedAccessGate) {
      return authenticatedAccessGate.destination;
    }
    const isManager =
      isMarketplaceOperatorRole(normalizedRole) ||
      normalizedRole === "independent_professional";
    if (isMarketplaceLogin) {
      return isManager ? getMarketplaceOperatorBookingsPath() : "/search";
    }
    return isManager ? "/manager/schedule" : "/my-bookings";
  })();

  const redirectTo = useCallback(
    (destination) => {
      if (!destination) return;
      if (/^https?:\/\//i.test(destination)) {
        window.location.assign(destination);
        return;
      }
      navigate(destination, { replace: true });
    },
    [navigate]
  );

  const resolveMarketplaceDestination = useCallback(
    (destination) => {
      if (!isMarketplaceLogin || !destination || /^https?:\/\//i.test(destination)) {
        return destination;
      }
      if (destination === "/superadmin" || destination.startsWith("/superadmin/")) {
        return buildHostUrl(runtimeBrand.marketplaceHostName, destination);
      }
      return destination;
    },
    [isMarketplaceLogin, runtimeBrand.marketplaceHostName]
  );

  const resolvedAuthenticatedDestination = resolveMarketplaceDestination(
    authenticatedDestination
  );

  const clearLoginWatchdog = useCallback(() => {
    if (loginWatchdogRef.current) {
      window.clearTimeout(loginWatchdogRef.current);
      loginWatchdogRef.current = null;
    }
  }, []);

  const prefetchPostLoginRoute = (targetRole) => {
    const normalizedTargetRole = (targetRole || "").toLowerCase();
    if (normalizedTargetRole === "technician") {
      void import("./TechnicianSchedule");
      return;
    }
    void import("./ManagerSchedule");
  };

  const refreshTechnicianOnboardingWithTimeout = async (accessToken) => {
    try {
      return await Promise.race([
        refreshTechnicianOnboarding({ accessToken }),
        new Promise((resolve) => {
          window.setTimeout(() => resolve(null), TECHNICIAN_REFRESH_TIMEOUT_MS);
        }),
      ]);
    } catch (err) {
      console.error("Unable to refresh technician onboarding status", err);
      return null;
    }
  };

  useEffect(() => {
    if (authLoading || !resolvedAuthenticatedDestination) return;
    clearLoginWatchdog();
    setLoading(false);
    redirectTo(resolvedAuthenticatedDestination);
  }, [
    authLoading,
    clearLoginWatchdog,
    redirectTo,
    resolvedAuthenticatedDestination,
  ]);

  useEffect(
    () => () => {
      clearLoginWatchdog();
    },
    [clearLoginWatchdog]
  );

  const setNumericAccountId = useCallback((value) => {
    setSalonId(String(value || "").replace(/\D/g, ""));
  }, []);

  const setLoginIdMode = useCallback((mode) => {
    if (mode === loginAccountMode) return;
    setLoginAccountMode(mode);
    setSalonId("");
    setError("");
    setMfaChallenge(null);
    setMfaCode("");
    setRememberDevice(false);
  }, [loginAccountMode]);

  if (!authLoading && resolvedAuthenticatedDestination) {
    return null;
  }

  const loginContent = isAccountingLogin
    ? ACCOUNTING_LOGIN_CONTENT
    : isMarketplaceLogin
    ? MARKETPLACE_LOGIN_CONTENT
    : OPS_LOGIN_CONTENT;
  const opsPublicHref = buildHostUrl(runtimeBrand.opsHost || "ellyanna.com", "/");
  const accountingPublicHref = buildHostUrl(
    runtimeBrand.accountingHostName || "salonbusinesscpa.com",
    "/"
  );
  const leftPrimaryHref = isAccountingLogin
    ? opsPublicHref
    : isMarketplaceLogin
    ? opsPublicHref
    : runtimeBrand.marketplaceHomeUrl ||
      buildHostUrl(runtimeBrand.marketplaceHostName || "myboothshare.com", "/");
  const leftTertiaryHref = !isAccountingLogin && !isMarketplaceLogin
    ? accountingPublicHref
    : null;
  const createAccountHref = isAccountingLogin || isMarketplaceLogin
    ? buildHostUrl(runtimeBrand.opsHost || "ellyanna.com", "/register")
    : "/register";

  const startWatchdog = () => {
    clearLoginWatchdog();
    setLoading(true);
    setError("");
    loginWatchdogRef.current = window.setTimeout(() => {
      setLoading(false);
      setError("Login is taking too long. Please try again.");
      loginWatchdogRef.current = null;
    }, LOGIN_FLOW_TIMEOUT_MS);
  };

  const finalizeSharedLogin = async (data) => {
    setMfaChallenge(null);
    setMfaCode("");
    setRememberDevice(false);
    const normalizedIncomingRole = (data.role || "").toLowerCase();
    let onboardingData = null;
    if (normalizedIncomingRole === "technician") {
      onboardingData = await refreshTechnicianOnboardingWithTimeout(data.access_token);
    }

    login(data.access_token, {
      ...data,
      salons:
        onboardingData?.accessible_salons ??
        data.salons ??
        data.accessibleSalons ??
        data.accessible_salons ??
        [],
      accessibleSalons:
        onboardingData?.accessible_salons ??
        data.salons ??
        data.accessibleSalons ??
        data.accessible_salons ??
        [],
      technician_status: onboardingData
        ? {
            profile_complete: onboardingData.profile_complete ?? false,
            staff_setup_complete: onboardingData.staff_setup_complete ?? null,
            has_selected_salon:
              onboardingData.has_selected_salon ?? Boolean(data.salon_id),
            salon_id: onboardingData.salon_id ?? data.salon_id ?? null,
            setup_complete: Boolean(onboardingData.staff_setup_complete ?? false),
          }
        : undefined,
    });

    if (data.must_change_password) {
      redirectTo("/change-password");
      return;
    }
    if (isMarketplaceLogin) {
      redirectTo(
        resolveMarketplaceDestination(
          next ||
            getMarketplaceAuthenticatedLandingPath({
              role: normalizedIncomingRole,
              user: data,
            })
        )
      );
      return;
    }
    if (isAccountingLogin) {
      redirectTo(next || "/salonbusinesscpa");
      return;
    }
    if (normalizedIncomingRole === "technician") {
      prefetchPostLoginRoute(normalizedIncomingRole);
      const hasSelectedSalon =
        onboardingData?.has_selected_salon ?? Boolean(data.salon_id);
      redirectTo(
        getTechnicianLandingPath({
          hasSelectedSalon,
          staffSetupComplete: onboardingData?.staff_setup_complete ?? false,
          profileComplete: onboardingData?.profile_complete ?? false,
        })
      );
      return;
    }
    if (
      !isMarketplaceLogin &&
      normalizedIncomingRole === "independent_professional" &&
      !data.salon_id
    ) {
      redirectTo(next || "/setup?mode=independent_professional");
      return;
    }
    if (data.superadmin_mode) {
      redirectTo(next || "/superadmin");
      return;
    }

    let onboardingCompleted;
    if (
      !isMarketplaceLogin &&
      data.salon_id &&
      !isBillingBypassedForSuperAdmin(data) &&
      isBillingEnforcedRole(normalizedIncomingRole)
    ) {
      try {
        const status = await getBusinessStatus(data.access_token, data.salon_id);
        onboardingCompleted = Boolean(
          status?.is_setup_complete ?? status?.onboarding_completed
        );
        localStorage.setItem(
          "onboarding_completed",
          onboardingCompleted ? "true" : "false"
        );
        notifyOnboardingStatusChange(onboardingCompleted);
      } catch (err) {
        console.warn("Failed to load business status", err);
      }
    }

    const loginAccessGate = !isMarketplaceLogin
      ? getEllyannaAccessGate(normalizedIncomingRole, data, {
          onboardingCompleted,
        })
      : null;

    if (loginAccessGate) {
      redirectTo(loginAccessGate.destination);
      return;
    }

    const isManager =
      isMarketplaceOperatorRole(normalizedIncomingRole) ||
      normalizedIncomingRole === "independent_professional";
    const destination = next
      ? next
      : isMarketplaceLogin
      ? isManager
        ? getMarketplaceOperatorBookingsPath()
        : "/search"
      : isManager
      ? "/manager/schedule"
      : "/my-bookings";

    if (isManager) {
      prefetchPostLoginRoute(normalizedIncomingRole);
    }

    const cachedOnboardingCompleted =
      (localStorage.getItem("onboarding_completed") || "").toLowerCase() === "true";
    notifyOnboardingStatusChange(cachedOnboardingCompleted);
    if (data.salon_id && !isBillingEnforcedRole(normalizedIncomingRole)) {
      void getBusinessStatus(data.access_token, data.salon_id)
        .then((status) => {
          const onboardingCompleted = Boolean(
            status?.is_setup_complete ?? status?.onboarding_completed
          );
          localStorage.setItem(
            "onboarding_completed",
            onboardingCompleted ? "true" : "false"
          );
          notifyOnboardingStatusChange(onboardingCompleted);
        })
        .catch((err) => {
          console.warn("Failed to load business status", err);
        });
    }
    redirectTo(destination);
  };

  const handleOperatorLogin = async (e) => {
    e.preventDefault();
    startWatchdog();
    if (!salonId.trim() || !identifier.trim()) {
      setError(
        `${
          loginAccountMode === "independent_professional"
            ? "Independent Professional ID"
            : "Salon ID"
        } and email or username are required.`
      );
      setLoading(false);
      clearLoginWatchdog();
      return;
    }
    if (!/^\d+$/.test(salonId.trim())) {
      setError(
        `${
          loginAccountMode === "independent_professional"
            ? "Independent Professional ID"
            : "Salon ID"
        } must be numeric.`
      );
      setLoading(false);
      clearLoginWatchdog();
      return;
    }
    try {
      const loginOptions =
        loginAccountMode === "independent_professional"
          ? { accountMode: "independent_professional" }
          : null;
      const data = loginOptions
        ? await loginApi(salonId.trim(), identifier.trim(), password, loginOptions)
        : await loginApi(salonId.trim(), identifier.trim(), password);
      if (!data || typeof data !== "object") {
        throw new Error("Unexpected login response. Refresh the page and try again.");
      }
      if (data.mfa_required) {
        setMfaChallenge(data);
        setMfaCode("");
        setRememberDevice(false);
        return;
      }
      await finalizeSharedLogin(data);
    } catch (err) {
      console.error("Login error:", err);
      const detail = err?.payload?.detail ?? err?.response?.data?.detail;
      const detailCode =
        detail && typeof detail === "object"
          ? detail.code || detail.detail || null
          : typeof detail === "string"
          ? detail
          : typeof err?.message === "string"
          ? err.message
          : null;
      if (detailCode === "EMAIL_VERIFICATION_REQUIRED") {
        const params = new URLSearchParams();
        if (identifier.includes("@")) {
          params.set("email", identifier.trim());
        }
        if (loginAccountMode === "independent_professional" && isMarketplaceLogin) {
          navigate(
            `/marketplace/verify-email${params.toString() ? `?${params}` : ""}`,
            { replace: true }
          );
          return;
        }
        if (salonId.trim()) {
          params.set("salonId", salonId.trim());
        }
        navigate(`/verify-email${params.toString() ? `?${params}` : ""}`, {
          replace: true,
        });
        return;
      }
      const errorMessage =
        detailCode && LOGIN_ERROR_MESSAGES[detailCode]
          ? LOGIN_ERROR_MESSAGES[detailCode]
          : detail && typeof detail === "object"
          ? detail.message
          : typeof detail === "string"
          ? detail
          : err?.status === 503
          ? "Sign-in is temporarily unavailable. Please try again in a few minutes."
          : err?.message === "Failed to fetch"
          ? "Unable to reach the sign-in service. Please try again."
          : err.message || "Login failed.";
      setError(errorMessage);
    } finally {
      clearLoginWatchdog();
      setLoading(false);
    }
  };

  const handleOperatorMfa = async (event) => {
    event.preventDefault();
    if (!mfaChallenge?.challenge_token) {
      setError("Start sign-in again to request a fresh verification code.");
      return;
    }
    startWatchdog();
    try {
      const data = await completeOperatorMfa(
        mfaChallenge.challenge_token,
        mfaCode.trim(),
        rememberDevice
      );
      await finalizeSharedLogin(data);
    } catch (err) {
      console.error("Operator MFA error:", err);
      setError(err.message || "Unable to verify the sign-in code.");
    } finally {
      clearLoginWatchdog();
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="operator-login-page"
      className="min-h-full overflow-visible flex flex-col bg-[radial-gradient(circle_at_top,_#fef3f2,_#f7ebe7_42%,_#eef2ff_100%)]"
    >
      <div className="flex min-h-full flex-col items-center justify-start px-4 pt-4 pb-8 lg:justify-center lg:py-8">
        <div className="w-full max-w-6xl">
          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div className="rounded-[28px] border border-white/70 bg-white/70 p-8 shadow-[0_20px_70px_rgba(47,53,102,0.12)] backdrop-blur">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-600">
                {loginContent.badge}
              </div>
              <h1 className="max-w-xl text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl">
                {loginContent.headline}
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
                {loginContent.body}
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {loginContent.infoCards.map((card) => (
                  <div
                    key={card.title}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm"
                  >
                    <div className="text-sm font-semibold text-slate-900">
                      {card.title}
                    </div>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      {card.body}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3 text-sm">
                <a
                  href={leftPrimaryHref}
                  className="inline-flex items-center justify-center rounded-full border border-[#2f3566]/20 bg-white px-4 py-2 font-semibold text-[#2f3566] transition hover:bg-[#2f3566]/5"
                >
                  {loginContent.primaryLinkLabel}
                </a>
                {leftTertiaryHref && loginContent.tertiaryLinkLabel ? (
                  <a
                    href={leftTertiaryHref}
                    className="inline-flex items-center justify-center rounded-full border border-[#2f3566]/20 bg-white px-4 py-2 font-semibold text-[#2f3566] transition hover:bg-[#2f3566]/5"
                  >
                    {loginContent.tertiaryLinkLabel}
                  </a>
                ) : null}
                <a
                  href={createAccountHref}
                  className="inline-flex items-center justify-center rounded-full border border-rose-200 bg-rose-50 px-4 py-2 font-semibold text-rose-600 transition hover:bg-rose-100"
                >
                  {loginContent.secondaryLinkLabel}
                </a>
              </div>
            </div>

            <div className="w-full rounded-[28px] border border-slate-200 bg-white px-5 py-5 shadow-[0_20px_60px_rgba(15,23,42,0.12)] md:px-7 md:py-7">
              <div className="mb-5">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2f3566]">
                  {loginContent.eyebrow}
                </div>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                  {loginContent.formHeadline}
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {loginContent.formBody}
                </p>
              </div>
              {mfaChallenge ? (
                <OperatorMfaForm
                  code={mfaCode}
                  emailMasked={mfaChallenge.email_masked}
                  error={error}
                  loading={loading}
                  onBack={() => {
                    setMfaChallenge(null);
                    setMfaCode("");
                    setRememberDevice(false);
                    setError("");
                  }}
                  onSubmit={handleOperatorMfa}
                  rememberDevice={rememberDevice}
                  setCode={setMfaCode}
                  setRememberDevice={setRememberDevice}
                />
              ) : (
                <>
                  <OperatorLoginForm
                    accountMode={loginAccountMode}
                    error={error}
                    forgotPasswordTarget={forgotPasswordTarget}
                    identifier={identifier}
                    loading={loading}
                    onSubmit={handleOperatorLogin}
                    password={password}
                    salonId={salonId}
                    setAccountMode={setLoginIdMode}
                    setIdentifier={setIdentifier}
                    setPassword={setPassword}
                    setSalonId={setNumericAccountId}
                    loginContent={loginContent}
                  />
                  <div className="mt-4 text-center text-sm text-slate-600">
                    {loginContent.footerLead}{" "}
                    <a
                      href={createAccountHref}
                      className="font-semibold text-rose-600 hover:text-rose-700"
                    >
                      {loginContent.footerLinkLabel}
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
